import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit8Notes: UnitNotes = {
  unitId: "unit-8",
  unitName: "Unit 8: Cold War and Decolonization (c. 1900–Present)",
  examWeight: "8–10%",
  focus: "How did the ideological struggle between capitalism and communism shape global politics, and how did colonial peoples achieve independence?",
  sections: [
    {
      id: "topic-8-1",
      title: "Topic 8.1: Setting the Stage for the Cold War and Decolonization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Shifting Balance of Power</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              After WWII, Europe was devastated and could no longer maintain vast empires. The US and USSR emerged as the only two superpowers.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇺🇸 United States</h4>
                <p className="text-sm text-slate-700 mt-1">Emerged economically dominant with intact manufacturing and possession of the atomic bomb.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇷🇺 Soviet Union</h4>
                <p className="text-sm text-slate-700 mt-1">Possessed the largest land army and occupied Eastern Europe.</p>
              </div>
              <KeyTerm 
                term="Ideological Conflict" 
                definition="The alliance between the US and USSR was purely pragmatic to defeat Hitler. Once Germany fell, the rift between Democracy/Capitalism and Communism/Authoritarianism reopened."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✊ The Rise of Anti-Imperialism</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🗣️ Self-Determination</h4>
                <p className="text-sm text-slate-700 mt-1">The UN Charter and rhetoric from both the US and USSR supported the right of people to choose their own government.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🪖 Colonial Contribution</h4>
                <p className="text-sm text-slate-700 mt-1">Colonies (India, African nations) had sent millions of troops to fight for Britain and France in WWII. They expected independence as a reward.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-bold text-yellow-900">💥 Weakness of Empire</h4>
                <p className="text-sm text-slate-700 mt-1">The Axis powers defeated European colonial powers early in WWII (e.g., Japan defeating the British in Singapore), destroying the myth of European invincibility.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-2",
      title: "Topic 8.2: The Cold War",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Ideological Struggle & Methodology</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A struggle over how to organize political and economic life. Since nuclear weapons made direct war suicidal (Mutually Assured Destruction), the war was fought through indirect means.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">🎯 Methods of Conflict</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Propaganda:</strong> Radio Free Europe vs. Soviet Pravda</li>
                  <li><strong>Espionage:</strong> CIA vs. KGB</li>
                  <li><strong>Alliances:</strong> NATO (West) vs. Warsaw Pact (East)</li>
                  <li><strong>Proxy Wars:</strong> Fighting in Korea, Vietnam, Angola, etc.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌏 The Non-Aligned Movement (NAM)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Many new nations in Africa and Asia did not want to be forced to choose a side. They formed the NAM to remain neutral and focus on developing their own economies.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🇮🇩 Sukarno (Indonesia)</h4>
                <p className="text-sm text-slate-700 mt-1">Hosted the <strong>Bandung Conference</strong> (1955), the birth of the NAM. He criticized both US and Soviet imperialism.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🇬🇭 Kwame Nkrumah (Ghana)</h4>
                <p className="text-sm text-slate-700 mt-1">Advocated for Pan-Africanism and avoiding neocolonialism.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇮🇳 Jawaharlal Nehru (India)</h4>
                <p className="text-sm text-slate-700 mt-1">Sought to keep India out of military alliances to focus on internal modernization.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-3",
      title: "Topic 8.3: Effects of the Cold War",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Military Alliances</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇺🇸 NATO</h4>
                <p className="text-sm text-slate-700 mt-1">North Atlantic Treaty Organization. US and Western Europe. An attack on one is an attack on all.</p>
              </div>
              <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇷🇺 Warsaw Pact</h4>
                <p className="text-sm text-slate-700 mt-1">USSR and Eastern European satellite states. Used to maintain Soviet control over the "Eastern Bloc."</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Proxy Wars</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Wars fought in local regions where the US and USSR supported opposing sides to gain influence without fighting directly.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">🇰🇷 Korean War (1950–1953)</h4>
                <p className="text-sm text-slate-700 mt-1">North (Communist/USSR & China backed) vs. South (Non-Communist/US & UN backed). Result: Stalemate/Split at 38th parallel.</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border-l-4 border-gray-400">
                <h4 className="font-bold text-gray-900">🇦🇴 Angolan Civil War (1975–2002)</h4>
                <p className="text-sm text-slate-700 mt-1">After independence from Portugal, three groups fought for control. The MPLA (Communist) was backed by the USSR and <strong>Cuba</strong>, while UNITA was backed by South Africa and the US.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">🇳🇮 Sandinista-Contras Conflict (Nicaragua)</h4>
                <p className="text-sm text-slate-700 mt-1">Socialist Sandinistas took power; the US backed the "Contras" (rebels) to overthrow them.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">☢️ Nuclear Proliferation</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">
                The arms race led to the development of Hydrogen bombs and ICBMs (Intercontinental Ballistic Missiles).
              </p>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🚀 Cuban Missile Crisis (1962)</h4>
                <p className="text-sm text-slate-700 mt-1">The closest the world came to nuclear war. USSR placed missiles in Cuba; US blockaded. Resolved diplomatically.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-4",
      title: "Topic 8.4: Spread of Communism After 1900",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇨🇳 The Chinese Revolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Internal tension between Nationalists (Kuomintang/Chiang Kai-shek) and Communists (CCP/Mao Zedong). Japanese aggression in WWII paused the civil war.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">✊ Communist Victory (1949)</h4>
                <p className="text-sm text-slate-700 mt-1">Mao gained support of the peasantry. Nationalists fled to Taiwan.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">📈 Great Leap Forward (1958)</h4>
                <p className="text-sm text-slate-700 mt-1">Attempt to rapidly industrialize and collectivize agriculture. Peasant lands were merged into communes. <strong>Result:</strong> Massive failure; estimated 20–45 million died from famine.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🎨 Cultural Revolution (1966)</h4>
                <p className="text-sm text-slate-700 mt-1">Attempt to purge capitalism and traditional culture. Red Guards (students) attacked teachers, intellectuals, and party officials.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌾 Movements to Redistribute Land</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In the Global South, land reform often became a proxy for "Communism" or "Socialism."
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇪🇹 Mengistu Haile Mariam (Ethiopia)</h4>
                <p className="text-sm text-slate-700 mt-1">Overthrew the monarchy (Haile Selassie), established a Marxist junta, and redistributed land to peasants. Famine and mismanagement followed.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇮🇷 White Revolution (Iran)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Non-Communist Example.</strong> The Shah of Iran implemented land reform (buying land from wealthy and selling to peasants) to undercut support for communist groups and modernize Iran from the top down.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇮🇳 Kerala (India)</h4>
                <p className="text-sm text-slate-700 mt-1">A state in India where voters elected a socialist government that successfully implemented land reform and educational investment.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-5",
      title: "Topic 8.5: Decolonization After 1900",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Negotiated Independence</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Occurred where colonial powers realized the cost of maintaining the colony outweighed the benefits, or where nationalist movements were well-organized politically.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇮🇳 India</h4>
                <p className="text-sm text-slate-700 mt-1">Led by <strong>Gandhi</strong> (civil disobedience) and the <strong>Indian National Congress</strong>. The British negotiated a withdrawal in 1947.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🇬🇭 Gold Coast (Ghana)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Kwame Nkrumah</strong> used strikes and petitions. Ghana became the first sub-Saharan African nation to gain independence (1957).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🇫🇷 French West Africa</h4>
                <p className="text-sm text-slate-700 mt-1">France slowly loosened control, allowing local rule, eventually leading to independence in 1960.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Independence through Armed Struggle</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Occurred where large numbers of European settlers lived (making the metropole reluctant to leave) or where colonial powers refused to cede control.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇩🇿 Algeria</h4>
                <p className="text-sm text-slate-700 mt-1">France considered Algeria part of France (not a colony). The <strong>FLN (National Liberation Front)</strong> fought a brutal guerilla war (1954–1962). France eventually withdrew.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇻🇳 Vietnam</h4>
                <p className="text-sm text-slate-700 mt-1">Ho Chi Minh (Communist) fought the French (failed to regain control after WWII) and then the US.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇦🇴 Angola</h4>
                <p className="text-sm text-slate-700 mt-1">Portuguese settlers refused to leave. Africans fought a long war until a coup in Portugal led to withdrawal.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕌 Regional/Religious Movements</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">☪️ Muslim League (British India)</h4>
                <p className="text-sm text-slate-700 mt-1">Muhammad Ali Jinnah argued for a separate state for Muslims (Pakistan).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇨🇦 Quebecois Separatist Movement</h4>
                <p className="text-sm text-slate-700 mt-1">French-speaking province in Canada attempted (peacefully) to separate.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-6",
      title: "Topic 8.6: Newly Independent States",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗺️ Redrawing Political Boundaries</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              New nations faced challenges from arbitrary borders, economic dependency, and migration.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🇮🇳 Partition of India (1947)</h4>
                <p className="text-sm text-slate-700 mt-1">India was split into India (Hindu majority) and Pakistan (Muslim majority). Resulted in mass migration, violence (1 million dead), and refugee crises.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇮🇱 Creation of Israel (1948)</h4>
                <p className="text-sm text-slate-700 mt-1">Britain withdrew from the Mandate of Palestine. The UN proposed a partition. War immediately broke out between the new state of Israel and Arab neighbors. Led to Palestinian displacement.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌍 Boundaries in Africa</h4>
                <p className="text-sm text-slate-700 mt-1">Colonial borders remained, grouping rival ethnic groups together or splitting groups apart, leading to conflict (e.g., Nigeria/Biafra Civil War).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💼 Governments Guiding Economic Life</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              New nations often used state direction to modernize.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇪🇬 Gamal Abdel Nasser (Egypt)</h4>
                <p className="text-sm text-slate-700 mt-1">Nationalized the Suez Canal; promoted Pan-Arabism and state-run industries.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇮🇳 Indira Gandhi (India)</h4>
                <p className="text-sm text-slate-700 mt-1">Nationalized banks and promoted the Green Revolution.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇹🇿 Julius Nyerere (Tanzania)</h4>
                <p className="text-sm text-slate-700 mt-1">Promoted <strong>Ujamaa</strong> (African Socialism), focusing on village cooperatives and self-reliance.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇱🇰 Sirimavo Bandaranaike (Sri Lanka)</h4>
                <p className="text-sm text-slate-700 mt-1">First female Prime Minister; implemented socialist economic policies.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Migration to Metropoles</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Former colonial subjects migrated to the imperial centers for work, maintaining cultural ties.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-900">🇬🇧 United Kingdom</h5>
                <p className="text-xs text-slate-700 mt-1">South Asians/Indians → London</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-900">🇫🇷 France</h5>
                <p className="text-xs text-slate-700 mt-1">Algerians/West Africans → Paris</p>
              </div>
              <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-900">🇺🇸 United States</h5>
                <p className="text-xs text-slate-700 mt-1">Filipinos → America</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-7",
      title: "Topic 8.7: Global Resistance to Established Power",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">☮️ Non-Violent Resistance</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🇮🇳 Mohandas Gandhi (India)</h4>
                <p className="text-sm text-slate-700 mt-1">Salt March, homespun movement.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇺🇸 Martin Luther King Jr. (USA)</h4>
                <p className="text-sm text-slate-700 mt-1">Civil Rights movement, desegregation.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🇿🇦 Nelson Mandela (South Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Led the fight against <strong>Apartheid</strong> (system of racial segregation). Initially advocated violence, later shifted to negotiation and reconciliation.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Intensification of Conflict & Dictatorships</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇨🇱 Augusto Pinochet (Chile)</h4>
                <p className="text-sm text-slate-700 mt-1">US-backed military dictator who overthrew a socialist government. Used violence against political opponents.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇪🇸 Francisco Franco (Spain)</h4>
                <p className="text-sm text-slate-700 mt-1">Fascist dictator who suppressed dissent.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇺🇬 Idi Amin (Uganda)</h4>
                <p className="text-sm text-slate-700 mt-1">"Butcher of Uganda," military dictator known for extreme brutality and expelling Asians from Uganda.</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">🎖️ Military-Industrial Complex</h4>
                <p className="text-sm text-slate-700 mt-1">Eisenhower warned that the relationship between the military and arms manufacturers was encouraging military buildup (US and USSR).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💣 Terrorism (Violence against civilians for political aims)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇵🇪 Shining Path (Peru)</h4>
                <p className="text-sm text-slate-700 mt-1">Communist terrorist group led by Abimael Guzmán.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🌍 Al-Qaeda</h4>
                <p className="text-sm text-slate-700 mt-1">Islamic fundamentalist group (Osama bin Laden) opposed to US intervention in the Middle East.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🇮🇪 IRA (Irish Republican Army)</h4>
                <p className="text-sm text-slate-700 mt-1">Used violence to try to force Britain out of Northern Ireland.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-8",
      title: "Topic 8.8: End of the Cold War",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Causes of the End</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🚀 Military and Technological Advances</h4>
                <p className="text-sm text-slate-700 mt-1">The US (under Reagan) increased military spending (e.g., SDI/"Star Wars"), forcing the USSR to try to keep up, which bankrupt their economy.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇦🇫 Failed Soviet Invasion of Afghanistan (1979–1989)</h4>
                <p className="text-sm text-slate-700 mt-1">Costly, demoralizing war; often called "Russia's Vietnam."</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">😤 Public Discontent</h4>
                <p className="text-sm text-slate-700 mt-1">Eastern Europeans were tired of economic hardship and repression.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💥 Collapse of the USSR (1991)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Mikhail Gorbachev introduced reforms to save the system, which actually accelerated the collapse.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🗣️ Glasnost</h4>
                <p className="text-sm text-slate-700 mt-1">"Openness" (more freedom of speech/press).</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🔧 Perestroika</h4>
                <p className="text-sm text-slate-700 mt-1">"Restructuring" (introducing some free-market elements).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🧱 Revolutions of 1989</h4>
                <p className="text-sm text-slate-700 mt-1">Eastern European satellite states (Poland, East Germany, etc.) declared independence. The Berlin Wall fell.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-9",
      title: "Topic 8.9: Causation in the Age of the Cold War and Decolonization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Summary of Unit 8 Themes</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">The global order shifted from European Empires → US/USSR Bipolarity → Global fragmentation/New Nations.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">👥 Social</h4>
                <p className="text-sm text-slate-700 mt-1">Rights discourses expanded (Civil Rights, Feminism), and social hierarchies were challenged through resistance.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🎨 Cultural</h4>
                <p className="text-sm text-slate-700 mt-1">Ideologies (Communism vs. Capitalism) defined global culture, but new nationalisms emerged in the post-colonial world.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">States struggled with how to develop—command economies (USSR/China) vs. free-market vs. mixed economies (India/Egypt).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Essential Comparison</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🔄 Continuity</h4>
                <p className="text-sm text-slate-700 mt-1">Struggle for resources and dominance continued from the imperial age.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">✨ Change</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>method</strong> of struggle shifted from direct colonial control to ideological influence and economic leverage (Neocolonialism).</p>
              </div>
            </div>
          </section>

          <ImportantNote>
            Unit 8 represents a pivotal shift in world history: from the age of European imperialism to a bipolar world defined by ideological conflict, and finally to a multipolar world of independent nations navigating their own paths.
          </ImportantNote>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit8Notes;